// Dealing with DEL side of the API
import axios from "axios";
import { getCsrfToken, setupCsrfInterceptor } from "../auth/csrfUtils";
import { getLocalDateString } from "../utils/dateUtils";

// Create axios instance with default config
const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  // Session cookies are required for this API
  withCredentials: true,
});

// Setup CSRF protection for this API instance
setupCsrfInterceptor(api);

// DEL API endpoints
const baseDelUrl = "/dels";
const baseReportsUrl = "/reports";

// Get all DELs
export const getAllDels = async () => {
  const response = await api.get(`${baseDelUrl}/reports`);
  return response.data;
};

// Get DEL Lists for user
export const getDelLists = async (
  employeeId,
  tableType,
  page = 1,
  perPage = 10,
  filters = null,
) => {
  const delFilters = filters
    ? {
        status: filters.status,
        del_date_start: filters.delStartDate,
        del_date_end: filters.delEndDate,
        submit_date_start: filters.submitStartDate,
        submit_date_end: filters.submitEndDate,
        approval_date_start: filters.approvalStartDate,
        approval_date_end: filters.approvalEndDate,
        project_number: filters.projectNumber,
        phase: filters.phase,
        project_manager: filters.projectManager,
        invoice_manager: filters.invoiceManager,
        created_by: filters.createdBy,
        approved_by: filters.approvedBy,
        created_date_start: filters.createdStartDate,
        created_date_end: filters.createdEndDate,
      }
    : {};
  const response = await api.post(`${baseDelUrl}/dels/lists`, {
    employee_id: employeeId,
    table_type: tableType,
    page: page,
    per_page: perPage,
    filters: delFilters,
  });
  return {
    del_list: response.data.del_list,
    total_count: response.data.total_count,
  };
};

// Get DEL information for a single DEL, managing Edit, View, and Copy modes
export const getDelInfo = async (delId, mode) => {
  const response = await api.get(
    `${baseDelUrl}/del_info?del_id=${delId}&mode=${mode}&client_date=${getLocalDateString()}`,
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Delete a DEL
export const deleteDel = async (delId, userContext) => {
  const response = await api.delete(
    `${baseDelUrl}/del_delete?del_id=${delId}&user_id=${userContext.employee_id}`,
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Submit a DEL - simplified payload (data already saved via autosave)
export const submitDel = async (payload) => {
  // Payload is just { del_id: "uuid" } - no compression needed
  const response = await api.post(`${baseDelUrl}/upload_submitted`, payload, {
    headers: {
      "X-CSRF-TOKEN": getCsrfToken(),
    },
  });

  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  }
  return response.data;
};

// Approve a DEL - simplified payload (data already saved via autosave)
export const approveDel = async (payload) => {
  // Payload is just { del_id: "uuid" } - no compression needed
  const response = await api.post(`${baseDelUrl}/approve_del`, payload, {
    headers: {
      "X-CSRF-TOKEN": getCsrfToken(),
    },
  });

  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  }
  return response.data;
};

// Create an empty draft DEL and return del_id
// clientIds ({ delId, jsaId, wasteDisposalId }) carries locally generated UUIDs so a
// draft started offline can be registered on the server under the same IDs on reconnect.
// The server treats a create with an existing del_id as idempotent.
export const createDelDraft = async (userContext, jsaVersion = 2, clientIds = null) => {
  const response = await api.post(
    `${baseDelUrl}/create_draft`,
    {
      user_id: userContext.employee_id,
      user_name: userContext.username,
      jsa_version: jsaVersion,
      client_date: getLocalDateString(),
      ...(clientIds?.delId ? { del_id: clientIds.delId } : {}),
      ...(clientIds?.jsaId ? { jsa_id: clientIds.jsaId } : {}),
      ...(clientIds?.wasteDisposalId
        ? { waste_disposal_id: clientIds.wasteDisposalId }
        : {}),
    },
    {
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
      },
    },
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Copy an existing DEL into a new draft (synchronous on server)
export const copyDel = async (
  sourceDelId,
  userContext,
  sourceTab = "drafts",
) => {
  const response = await api.post(
    `${baseDelUrl}/copy`,
    {
      source_del_id: sourceDelId,
      user_id: userContext.employee_id,
      user_name: userContext.username,
      source_tab: sourceTab,
      client_date: getLocalDateString(),
    },
    {
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
      },
    },
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Reject a DEL - simplified payload
export const rejectDel = async (delId, rejectedReason, userContext) => {
  const response = await api.post(
    `${baseDelUrl}/reject_del`,
    {
      del_id: delId,
      rejected_reason: rejectedReason,
      user_id: userContext.employee_id,
    },
    {
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
      },
    },
  );

  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  }
  return response.data;
};

// Transfer a DEL
export const transferDel = async (
  delId,
  transEmpId,
  createdByEmpId,
  userContext,
) => {
  const response = await api.post(`${baseDelUrl}/transfer_del`, {
    del_id: delId,
    trans_emp_id: transEmpId,
    created_by_emp_id: createdByEmpId,
    user_id: userContext.employee_id,
  });
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Transfer a DEL approval
export const transferDelApproval = async (delId, transEmpId) => {
  const response = await api.post(`${baseDelUrl}/transfer_del_approval`, {
    del_id: delId,
    trans_emp_id: transEmpId,
  });
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Unapprove a DEL
export const unapproveDel = async (delId, userContext) => {
  console.log("Unapprove DEL Data:", {
    del_id: delId,
    user_id: userContext.employee_id,
  });
  const response = await api.post(`${baseDelUrl}/unapprove_del`, {
    del_id: delId,
    user_id: userContext.employee_id,
  });
  console.log("Response:", response.data);
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};
